import _ from 'lodash';
import type { GenericRecord } from 'interface/common';
import type { FalsyTransformInput } from 'interface/object';

export const withoutAttrs = (obj: GenericRecord, attrsToExclude: string[]): GenericRecord => {
  const result: GenericRecord = {};

  Object.keys(obj).forEach((key) => {
    if (!attrsToExclude.includes(key)) {
      result[key] = obj[key];
    }
  });

  return result;
};

export const withOnlyAttrs = (obj: GenericRecord, attrsToInclude: string[]): GenericRecord => {
  const result: GenericRecord = {};

  Object.keys(obj).forEach((key) => {
    if (attrsToInclude.includes(key)) {
      result[key] = obj[key];
    }
  });

  return result;
};

/**
 * Splits an array into two partitions based on the given condition.
 *
 * @param array
 * @param callback
 * @param interceptor
 *
 * @returns */
export const splitArray = <T>(
  array: T[],
  callback: (element: T) => boolean = () => true,
  interceptor?: (element: T) => void
): [T[], T[]] => {
  return array.reduce(
    (result, element) => {
      if (interceptor) interceptor(element);
      result[callback(element) ? 0 : 1].push(element);
      return result;
    },
    [[], []]
  );
};

/**
 * Check if the given object is empty or not.
 *
 * @param obj
 * @returns */
export function isObjectEmpty(obj?: object | null): boolean {
  if (!obj) {
    return true;
  }

  return obj.constructor === Object && Object.entries(obj).length === 0;
}

/**
 * Join all the values in objects with given separator.
 * for eg: { firstName: 'Bob', lastName: 'Marley'} returns 'Bob Marley'.
 *
 * @param obj
 * @param separator
 * @returns */
export function joinValues(obj: GenericRecord, separator = ' '): string {
  return Object.values(obj).join(separator);
}

/**
 * Deeply remove empty values from the given object.
 *
 * @param obj
 * In case you want to exclude any false value like "", 0, false, etc.
 * @param excludedValues
 * @returns
 */
export function deepFalsyTransform(
  obj: FalsyTransformInput,
  targetValue: string | number | boolean | null = null,
  excludedValues: Array<string | number | boolean | null | undefined> = []
): FalsyTransformInput {
  if (Array.isArray(obj)) {
    return obj.map((item) => deepFalsyTransform(item, targetValue, excludedValues)) as FalsyTransformInput;
  }

  if (!_.isObject(obj)) {
    if (excludedValues.includes(obj as string | number | boolean | null | undefined)) {
      return obj;
    }

    return obj || targetValue;
  }

  const data = _.mapValues(obj, (data) => {
    if (excludedValues.includes(data)) {
      return data;
    }

    return data || targetValue;
  });

  return data;
}

/**
 * Combines array of objects to single array.
 *
 * @param array
 * @returns */
export function combineArrayObjects(array: GenericRecord[]): GenericRecord {
  return array.reduce((result: GenericRecord, obj) => {
    return { ...result, ...obj };
  }, {});
}

/**
 * Check if key exists on an object
 *
 * @param object
 * @param key
 */
export function hasKey(object: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(object, key);
}

/**
 * Convert snake case keys to camel case.
 *
 * @param obj
 * @returns */
export function snakeToCamel(obj: GenericRecord): GenericRecord {
  return _.mapKeys(obj, (_value, key) => _.camelCase(key)) as GenericRecord;
}

/**
 * Get name and value from key and value
 * Only use to edit form with react-hook-form
 */
export const getNameAndValue = (
  object: GenericRecord,
  state: (name: string, value: string | number | boolean | null | undefined) => void
): void => {
  Object.entries(object).forEach(([name, value]) => state(name, value));
};
